import React, { useEffect, useState } from "react";
import NewStationFormView from "./NewStationFormView/NewStationFormView";
import { UserRole, userRoles } from "../../../../user/types/user.types";
import inputValidityChecker from "../../../../../utils/inputValidityChecker";
import { StationPatch } from "../../../types/stations.types";
import useStations from "../../../hooks/useStation";
import { useAuth } from "../../../../auth/hooks";
import { extractAxoisErrorsMessage } from "../../../../errorHandler/utils/errors.utils";
import { useNavigate } from "react-router-dom";
import useHardware from "../../../../hardwares/hooks/useHardware";
import useSingleStation from "../../../hooks/useSingleStation";
import { Organization } from "../../../../organization/types/organization.types";
import OrganizationPicker from "../../../../organization/components/OrganizationPicker/OrganizationPicker";
import useSingleOrganization from "../../../../organization/hooks/useSingleOrganization";

export interface StationFormData {
  name?: string;
  city?: string;
  country?: string;
  hardwareId?: string;
  organizationId?: string;
  street?: string;
  zipCode?: string;
  latitude?: string;
  longitude?: string;
  deviceId?: string;
  slug?: string;

}

const INITIAL_STATION_FORM_DATA: StationFormData = {
  name: "",
  city: "",
  country: "",
  hardwareId: "",
  deviceId: undefined,
  organizationId: "",
  street: "",
  zipCode: "",
  latitude: "0",
  longitude: "0",
  slug: "",
};

export interface StationFormErrors {
  isErrorName: boolean;
  isErrorCity: boolean;
  isErrorCountry: boolean;
  isErrorHardwareId: boolean;
  isErrorOrganizationId: boolean;

  isErrorStreet: boolean;
  isErrorZipCode: boolean;
  isErrorLatitude: boolean;
  isErrorLongitude: boolean;
  isErrorSlug: boolean;


  allGood: boolean;
  errorMessage: string | null;

}

const INITIAL_STATION_ERROR: StationFormErrors = {
  isErrorName: false,
  isErrorCity: false,
  isErrorCountry: false,
  isErrorHardwareId: false,
  isErrorOrganizationId: false,
  isErrorLatitude: false,
  isErrorLongitude: false,
  isErrorStreet: false,
  isErrorZipCode: false,
  isErrorSlug: false,


  allGood: true,
  errorMessage: null,
};

export const checkIsValid = (data: StationPatch, userRole: UserRole) => {
  const errors = { ...INITIAL_STATION_ERROR };
  let messageList = [];

  if (!inputValidityChecker.validateNotEmptyText(data.station_hardware_id)) {
    errors.isErrorHardwareId = true;
    errors.allGood = false;
    messageList.push("L'Device ID de la station est obligatoire");
  }

  if (!inputValidityChecker.validateNotEmptyText(data.station_name)) {
    errors.isErrorName = true;
    errors.allGood = false;
    messageList.push("Le nom de la station est obligatoire");
  }

  if (
    !inputValidityChecker.validateNotEmptyText(data.station_city) &&
    userRole < userRoles.SUPER_ADMIN
  ) {
    errors.isErrorCity = true;
    errors.allGood = false;
    messageList.push("La ville de la station est obligatoire");
  }

  if (
    !inputValidityChecker.validateNotEmptyText(data.station_country) &&
    userRole < userRoles.SUPER_ADMIN
  ) {
    errors.isErrorCountry = true;
    errors.allGood = false;
    messageList.push("Le pays de la station est obligatoire");
  }

  if (!data.station_organization_id && userRole < userRoles.SUPER_ADMIN) {
    errors.isErrorOrganizationId = true;
    errors.allGood = false;
    messageList.push("L'organisation de la station est obligatoire");
  }

  if (messageList.length > 0) {
    errors.errorMessage = messageList.join(", ").concat(".");
  }


  if (!inputValidityChecker.validateNumberFloat(data.station_location_lat+"")) {
    errors.isErrorLatitude = true;
    errors.allGood = false;
    messageList.push("La latitude de la station est obligatoire");
  }

  if (!inputValidityChecker.validateNumberFloat(data.station_location_lng+"")) {
    errors.isErrorLongitude = true;
    errors.allGood = false;
    messageList.push("La longitude de la station est obligatoire");

  }



  return errors;
};

const converteStationFormDataToStationPatch = (
  data: StationFormData
): StationPatch => {
  return {
    station_name: data.name,
    station_city: data.city,
    station_country: data.country,
    station_hardware_id: data.hardwareId,
    station_organization_id: data.organizationId,
    station_street: data.street,
    station_zip_code: data.zipCode,
    station_location_lat: data.latitude ? parseFloat(data.latitude) : 0,
    station_location_lng: data.longitude ? parseFloat(data.longitude) : 0,
    station_slug: data.slug,

  };
};

const errorMessageTraductor = (error: string) => {
  if (error.includes("station_name")) {
    return "Le nom de la station est obligatoire";
  }
  if (error.includes("CODE_ERROR_APP:3-2")) {
    return "handware Id est déjà utilisé par une autre station";
  }

  if (error.includes("decoding request body")) {
    return "Le formulaire n'est pas valid. Vérifiez les champs.";
  }
  return error;
};
export default function NewStationFormPresenter({
  onSuccess,
  organizationId,
  idStationToUpdate,
  isUpdate: isUpdateRaw
}: {
  onSuccess?: () => void;
  organizationId?: string;
  idStationToUpdate?: string;
  isUpdate?: boolean;
}) {
  const isUpdate = isUpdateRaw || (!!idStationToUpdate);
  const [formData, setFormData] = React.useState<StationFormData>({
    ...INITIAL_STATION_FORM_DATA,
    organizationId,
  });
  const [errors, setErrors] = React.useState<StationFormErrors>({
    ...INITIAL_STATION_ERROR,
  });
  const [, setIsSubmitting] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<
    string | null | undefined
  >(null);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const { createNewStationQuery } = useStations({ organizationId });
  const { findHardwareByDevUID,  getHardwareById} = useHardware();
  const { updateStationQuery, singleStationQuery } = useSingleStation(idStationToUpdate);

  const [organization, setOrganization] = useState<Organization| undefined>()

  const navigation = useNavigate();
  const { userRole } = useAuth();

  const {singleOrganizationQuery} = useSingleOrganization(singleStationQuery.data?.station_organization_id || undefined);

  useEffect(() => {
    if (singleOrganizationQuery.data) {
      setOrganization(singleOrganizationQuery.data);
    }
  }, [singleOrganizationQuery.data]);

  useEffect(() => {
    if (isUpdate && singleStationQuery.data) {
 
      const data = singleStationQuery.data;
      setFormData({
        name: data.station_name,
        city: data.station_city,
        country: data.station_country,
        organizationId: data.station_organization_id,
        street: data.station_street,
        zipCode: data.station_zip_code,
        latitude: data.station_location_lat+"",
        longitude: data.station_location_lng+"",
        slug: data.station_slug,
      });

      getHardwareById(singleStationQuery.data.station_hardware_id || "").then((res) => {
        if (res){
          setFormData((prev) => ({ ...prev, deviceId: res.hardware_device_id }));
        }
      }).catch((err) => {
        console.log(err)
      })
  
    }
  },[]) // eslint-disable-line

  const handleChange = (key: keyof StationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const data = converteStationFormDataToStationPatch({...formData, organizationId: organization?.organization_id});


    try{
      setIsLoading(true);
      const hardwareFound  = await findHardwareByDevUID(formData.deviceId || "none");
    
      setIsLoading(false);

      if (!hardwareFound){
        setErrorMessage("Device ID n'existe pas");
        setErrors({...INITIAL_STATION_ERROR, isErrorHardwareId: true, allGood: false});
        return;
 
      }

    const dataFormated = {...data, station_hardware_id: hardwareFound?.hardware_id};
    const errors = checkIsValid(dataFormated , userRoles.SUPER_ADMIN);
    if (errors.allGood) {


      if (isUpdate && idStationToUpdate) {
        updateStationQuery.mutate({
          data: {...singleStationQuery.data ,...dataFormated },
          callback: ({ result, error }) => {
            if (error) {
              const errorDesc = extractAxoisErrorsMessage(error);
              setErrorMessage(errorMessageTraductor(errorDesc));
            }
            if (result) {
              onSuccess && onSuccess();
            }
            setIsSubmitting(false);
          },
        });
        return;
      }

      if(!isUpdate){
      createNewStationQuery.mutate({
        newData: {...dataFormated},
        callback: ({ result, error }) => {
          if (error) {
            const errorDesc = extractAxoisErrorsMessage(error);

            setErrorMessage(errorMessageTraductor(errorDesc));
          }
          if (result) {
            onSuccess && onSuccess();
            navigation(result.station_id);
          }
          setIsSubmitting(false);
        },
      });
    }
    } else {
      setErrors(errors);
      setErrorMessage(errors.errorMessage);
    }
    setIsSubmitting(false);
  }catch(e){
    setIsLoading(false);
    console.log(e)
  }
  };

  return (
    <NewStationFormView
      currentUserRoleCreator={userRole}
      formData={formData}
      setFormData={handleChange}
      errorStatus={errors}
      messageError={errorMessage}
      isLoading={createNewStationQuery.isLoading || isLoading}
      handleSubmit={handleSubmit}
      isUpdate={isUpdate}
      submitLabel={isUpdate ? "Modifier" : "Créer"}
      description={!isUpdate ?"Saisissez les informations de la nouvelle station" : "Modifier les informations de la station"}
      organizationPickerRender={() => <OrganizationPicker setOrganization={setOrganization}  currentOrganization={organization}/>}
    />
  );
}
